import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * The tests a veterinarian has picked up from the assistant's answer.
 *
 * The assistant names the test that would settle each open question, and links it to
 * the Laboklin service that provides it. This holds what they tapped, so the drawer
 * can show a running basket and hand it on to MyLab's own order form.
 *
 * Deliberately in-memory and per-session: nothing here places an order. It is a
 * priced basket the veterinarian assembles while reading, and abandons by closing
 * the drawer.
 */

export interface BasketItem {
  serviceNo: number;
  /** What the assistant called it, as it appeared in the sentence. */
  name: string;
  /** Euros. Null when the assistant linked a service without a price. */
  price: number | null;
  /** The question this test was offered to answer, for the basket to show. */
  reason?: string;
}

interface LabBasketValue {
  items: BasketItem[];
  add: (item: BasketItem) => void;
  remove: (serviceNo: number) => void;
  clear: () => void;
  has: (serviceNo: number) => boolean;
  total: number | null;
}

const LabBasketContext = createContext<LabBasketValue | null>(null);

export const LabBasketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<BasketItem[]>([]);

  const add = useCallback((item: BasketItem) => {
    setItems((prev) => (prev.some((i) => i.serviceNo === item.serviceNo) ? prev : [...prev, item]));
  }, []);

  const remove = useCallback((serviceNo: number) => {
    setItems((prev) => prev.filter((i) => i.serviceNo !== serviceNo));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<LabBasketValue>(() => {
    // A total is only honest when every line carries a price; one unpriced service
    // would make the sum quietly wrong.
    const priced = items.every((i) => typeof i.price === 'number');
    return {
      items,
      add,
      remove,
      clear,
      has: (serviceNo: number) => items.some((i) => i.serviceNo === serviceNo),
      total: items.length > 0 && priced ? items.reduce((n, i) => n + (i.price ?? 0), 0) : null,
    };
  }, [items, add, remove, clear]);

  return <LabBasketContext.Provider value={value}>{children}</LabBasketContext.Provider>;
};

/**
 * Returns null outside a provider, so the chat renders the same links as plain text
 * in the GekkoVet app where there is no laboratory basket to add to.
 */
export const useLabBasket = (): LabBasketValue | null => useContext(LabBasketContext);
